import { useBluelagoon } from "@bluelagoon";
import { useAuth } from "../context/AuthContext";
import React, { useState, useEffect } from "react";

// Billing plans configuration with workflow limits
const BILLING_PLANS = [
    {
        name: "Starter",
        priceIds: [
            "price_1R95yJKVJm3st1cQ6BdBy3Mw",
            "price_1R94xZKVJm3st1cQQZSVWO0d",
        ],
        workflowLimit: 999999,
    },
    {
        name: "Professional",
        priceIds: [
            "price_1R95yWKVJm3st1cQRMYPloyQ",
            "price_1R4LGzKVJm3st1cQsPsFA4SC",
        ],
        workflowLimit: 99999,
    },
    {
        name: "Advanced",
        priceIds: [
            "price_1RJgPeKVJm3st1cQGLBXj5sx",
            "price_1RJgPJKVJm3st1cQjvcYbYIs",
        ],
        workflowLimit: 99999,
    },
    {
        name: "Enterprise",
        priceIds: ["enterprise"],
        workflowLimit: Infinity, // Unlimited
    },
];

/**
 * Custom hook to check workflow limits based on user's subscription plan
 * @returns {Object} Object containing limit info and check functions
 */
export function useWorkflowLimits() {
    const { user, isInTrial, trialStatus, activePlanSubscription } = useAuth();
    const [activeWorkflowCount, setActiveWorkflowCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { call: getWorkflowCount } = useBluelagoon({
        method: "workflowCount",
    });

    // Get current subscription plan
    const currentSubscription = activePlanSubscription;

    const currentPriceId = currentSubscription?.priceId;

    // Find matching plan based on priceId
    const currentPlan = BILLING_PLANS.find((plan) =>
        plan.priceIds.includes(currentPriceId)
    );

    // Determine workflow limit
    const getWorkflowLimit = () => {
        // Trial users get Starter limits
        if (isInTrial) {
            return (
                BILLING_PLANS.find((plan) => plan.name === "Starter")
                    ?.workflowLimit || 5
            );
        }

        // Subscribed users get their plan's limit
        if (currentPlan) {
            return currentPlan.workflowLimit;
        }

        // No subscription = Starter limits
        return (
            BILLING_PLANS.find((plan) => plan.name === "Starter")
                ?.workflowLimit || 5
        );
    };

    const workflowLimit = getWorkflowLimit();

    // Fetch current active workflow count
    useEffect(() => {
        const fetchActiveWorkflowCount = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await getWorkflowCount({
                    status: "ACTIVE",
                });

                if (response?.total !== undefined) {
                    setActiveWorkflowCount(response.total);
                } else {
                    setError("Failed to get workflow count");
                }
            } catch (err) {
                console.error("Error fetching workflow count:", err);
                setError("Failed to load workflow count");
            } finally {
                setLoading(false);
            }
        };

        if (user?.company) {
            fetchActiveWorkflowCount();
        }
    }, [getWorkflowCount, user?.company]);

    const isAtLimit = activeWorkflowCount >= workflowLimit;
    const canCreateWorkflow = !isAtLimit;
    const remainingWorkflows = Math.max(0, workflowLimit - activeWorkflowCount);

    return {
        // Current state
        activeWorkflowCount,
        workflowLimit,
        currentPlan: currentPlan?.name || "Starter",

        // Status checks
        isAtLimit,
        canCreateWorkflow,
        remainingWorkflows,

        // Loading states
        loading,
        error,

        // Helper function to refresh count
        refreshCount: React.useCallback(() => {
            const fetchActiveWorkflowCount = async () => {
                try {
                    const response = await getWorkflowCount({
                        status: "ACTIVE",
                    });

                    if (response?.total !== undefined) {
                        setActiveWorkflowCount(response.total);
                    }
                } catch (err) {
                    console.error("Error refreshing workflow count:", err);
                }
            };

            if (user?.company) {
                fetchActiveWorkflowCount();
            }
        }, [getWorkflowCount, user?.company]),
    };
}
